/** Canvas Yjs schema version. Bumped on breaking changes. */
export const SCHEMA_VERSION = 1;

/** Search-extraction debounce timings. See data-layer.md §6. */
export const EXTRACTION_DEBOUNCE_SOFT_MS = 1500;
export const EXTRACTION_DEBOUNCE_HARD_MS = 30_000;

/**
 * Shared color swatches used in the bottom toolbar for sticky notes and freedraw.
 * Pastels are the tonal anchor for the canvas palette.
 */
export const TOOLBAR_SWATCHES = [
  { name: 'Yellow', hex: '#FEF9C3' },
  { name: 'Red',    hex: '#FEE2E2' },
  { name: 'Green',  hex: '#DCFCE7' },
  { name: 'Blue',   hex: '#DBEAFE' },
  { name: 'Purple', hex: '#EDE9FE' },
  { name: 'Pink',   hex: '#FCE7F3' },
  { name: 'Orange', hex: '#FED7AA' },
  { name: 'White',  hex: '#FFFFFF' },
] as const;

/** @deprecated Use TOOLBAR_SWATCHES */
export const STICKY_COLORS = TOOLBAR_SWATCHES;

export const DEFAULT_STICKY_COLOR = '#FEF9C3';

/**
 * Unified 20-color canvas palette — 2 rows × 11 columns.
 * Row 1: 11 colors. Row 2: 9 colors + none + custom picker (cols 10–11).
 *
 * Row 1 — neutrals + vivid spectrum
 * Row 2 — pastels (sticky colors anchored left — do not change these)
 */
export const CANVAS_COLORS = [
  // Row 1 — neutrals then vivid spectrum (11 colors)
  '#1A1A1A', '#B0ADB8', '#FFFFFF', '#EF4444', '#F97316', '#EAB308', '#22C55E', '#14B8A6', '#3B82F6', '#8B5CF6', '#EC4899',
  // Row 2 — pastels, sticky palette anchored left (10 colors; col 11 is custom picker)
  '#FEF9C3', '#FEE2E2', '#DCFCE7', '#DBEAFE', '#EDE9FE', '#FCE7F3', '#FED7AA', '#D1FAE5', '#E0F2FE', '#EAE8F4',
] as const;

/**
 * Default text / stroke color stored in Yjs node data.
 * Mirrors --on-surface from globals.css.
 */
export const DEFAULT_NODE_COLOR = '#2F3331'; // --on-surface

/** Default fill for new shape nodes. */
export const DEFAULT_SHAPE_FILL = '#FFFFFF'; // --surface

/** Default stroke color for new shape nodes — design-token gray (matches placement-ghost stroke). */
export const DEFAULT_SHAPE_STROKE = 'var(--outline-variant)';

/** Gap between source node edge and the newly placed node when using expand dots (canvas units). */
export const EXPAND_GAP = 64;

/** Minimum dimensions for each resizable node type (canvas units). */
export const NODE_MIN_SIZE = {
  sticky:  { width: 120, height: 140 },
  shape:   { width: 40,  height: 40  },
  text:    { width: 120, height: 24  },
  frame:   { width: 120, height: 80  },
} as const;

/** Rendered dimensions of a collapsed frame (canvas units). The frame's
 *  persisted `size` keeps the open value so reopening restores the user's
 *  layout; the snapshot just overrides width/height while collapsed so
 *  React Flow's selection bounds + resize handles trace the folder, not
 *  the open rectangle. */
export const FRAME_COLLAPSED_SIZE = { width: 80, height: 80 } as const;

/** Freedraw stroke size presets (stroke-width in canvas units). */
export const FREEDRAW_SIZES = [
  { label: 'Thin',   value: 2  },
  { label: 'Medium', value: 6  },
  { label: 'Thick',  value: 16 },
] as const;

export const DEFAULT_FREEDRAW_SIZE = 2;

/** Default stroke width for the highlighter tool — much thicker than pen. */
export const DEFAULT_HIGHLIGHTER_SIZE = 24;

/** Stroke opacity for the highlighter tool. Pen mode uses 1.0; highlighter
 *  is semi-transparent so overlapping strokes blend the way a physical
 *  highlighter does. */
export const HIGHLIGHTER_OPACITY = 0.4;

/** Pixel offset applied to a duplicated node's position so it's visually
 *  distinct from the original. Applies on Cmd-D, context-menu Duplicate,
 *  and multi-select duplicate. */
export const DUPLICATE_OFFSET = 20;

export const DEFAULT_FREEDRAW_COLOR = '#1A1A1A';
